import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, LogIn, LogOut, PackagePlus, PackageMinus, ClipboardCheck } from "lucide-react";

export function Attendance() {
  const { t, isAr, fmtDate } = useI18n();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/attendance").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { api.get("/branches").then((r) => { setBranches(r.data); setBranchId(r.data[0]?.id || ""); }); load(); }, []);

  const checkin = () => {
    if (!navigator.geolocation) { toast.error(isAr ? "الموقع غير مدعوم" : "Geolocation unsupported"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post("/admin/attendance/checkin", { branch_id: branchId, lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(`${data.kind === "in" ? (isAr ? "تسجيل حضور" : "Checked in") : (isAr ? "تسجيل انصراف" : "Checked out")} · ${data.distance_m}م`);
        load();
      } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    }, () => { toast.error(isAr ? "تعذّر تحديد الموقع" : "Location denied"); setBusy(false); }, { enableHighAccuracy: true });
  };

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{isAr ? "حضور الموظفين" : "Staff Attendance"}</h1>
      <p className="mt-1 text-sm text-slate-500">{isAr ? "يشترط أن تكون ضمن 100 متر من الفرع" : "Must be within 100m of the branch"}</p>
      <div className="mt-5 max-w-md rounded-2xl border border-border bg-white p-6">
        <label className="text-sm font-medium text-slate-700">{isAr ? "الفرع" : "Branch"}</label>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="attendance-branch" className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 text-sm">
          {branches.map((b) => <option key={b.id} value={b.id}>{isAr ? b.name_ar : b.name_en}</option>)}
        </select>
        <button onClick={checkin} disabled={busy} data-testid="checkin-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-50">
          <MapPin className="h-4 w-4" /> {busy ? t("common.loading") : (isAr ? "تسجيل الحضور/الانصراف" : "Check in / out")}
        </button>
      </div>
      <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), isAr ? "النوع" : "Type", isAr ? "المسافة" : "Distance", isAr ? "الوقت" : "Time"].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium">{r.staff_name}</td>
              <td className="px-4 py-3">{r.kind === "in" ? <span className="inline-flex items-center gap-1 text-green-600"><LogIn className="h-3.5 w-3.5" /> {isAr ? "حضور" : "In"}</span> : <span className="inline-flex items-center gap-1 text-[hsl(var(--brand-red))]"><LogOut className="h-3.5 w-3.5" /> {isAr ? "انصراف" : "Out"}</span>}</td>
              <td className="px-4 py-3">{r.distance_m}م</td>
              <td className="px-4 py-3 text-slate-500">{fmtDate(r.at).greg} {fmtDate(r.at).time}</td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <div className="py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>
    </div>
  );
}

export function InventoryPro() {
  const { t, isAr, localize } = useI18n();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [inv, setInv] = useState([]);
  const [moves, setMoves] = useState([]);
  const [modal, setModal] = useState(null); // {product, type}
  const [qty, setQty] = useState("");
  const load = (bid) => {
    const q = bid ? `?branch_id=${bid}` : "";
    api.get(`/admin/inventory${q}`).then((r) => setInv(r.data)).catch(() => {});
    api.get(`/admin/inventory/movements${q}`).then((r) => setMoves(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/branches").then((r) => { setBranches(r.data); const b = r.data[0]?.id || ""; setBranchId(b); load(b); }); }, []);
  const onBranch = (b) => { setBranchId(b); load(b); };

  const submit = async () => {
    try {
      await api.post("/admin/inventory/movement", { product_id: modal.product.product_id, branch_id: branchId, type: modal.type, qty: parseFloat(qty) });
      toast.success(t("common.save")); setModal(null); setQty(""); load(branchId);
    } catch (e) { toast.error(apiError(e)); }
  };
  const totIn = moves.filter((m) => m.direction === "in").reduce((s, m) => s + m.qty, 0);
  const totOut = moves.filter((m) => m.direction === "out").reduce((s, m) => s + m.qty, 0);
  const TYPES = [["purchase", isAr ? "شراء (وارد)" : "Purchase (in)", PackagePlus], ["sale", isAr ? "صرف (صادر)" : "Issue (out)", PackageMinus], ["stocktake", isAr ? "جرد" : "Stocktake", ClipboardCheck]];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-slate-900">{isAr ? "المخزون والجرد" : "Inventory & Stocktake"}</h1>
        <select value={branchId} onChange={(e) => onBranch(e.target.value)} data-testid="inv-branch" className="rounded-lg border border-border px-3 py-1.5 text-sm">{branches.map((b) => <option key={b.id} value={b.id}>{isAr ? b.name_ar : b.name_en}</option>)}</select>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[[isAr ? "إجمالي الوارد" : "Total in", totIn, "text-green-600"], [isAr ? "إجمالي الصادر" : "Total out", totOut, "text-[hsl(var(--brand-red))]"], [isAr ? "الأصناف" : "Items", inv.filter((r) => r.product).length, "text-primary"]].map(([l, v, c], i) => (
          <div key={i} className="rounded-xl border border-border bg-white p-4"><div className={`text-2xl font-extrabold ${c}`}>{v}</div><div className="text-xs text-slate-500">{l}</div></div>
        ))}
      </div>
      <div className="mt-5 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), isAr ? "الرصيد" : "Balance", isAr ? "حد أدنى" : "Min", ""].map((h, i) => <th key={i} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{inv.filter((r) => r.product).map((r) => (
            <tr key={r.id} data-testid={`inv-row-${r.product_id}`} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium">{r.product ? localize(r.product, "name") : r.product_id}</td>
              <td className="px-4 py-3"><span className={r.stock <= (r.min_stock || 0) ? "font-bold text-[hsl(var(--brand-red))]" : "font-medium"}>{r.stock}</span></td>
              <td className="px-4 py-3 text-slate-400">{r.min_stock || 0}</td>
              <td className="px-4 py-3"><div className="flex justify-end gap-1">{TYPES.map(([tp, label, Icon]) => (
                <button key={tp} onClick={() => setModal({ product: r, type: tp })} data-testid={`mv-${tp}-${r.product_id}`} title={label} className="rounded-md border border-border p-1.5 text-slate-500 hover:border-primary hover:text-primary"><Icon className="h-3.5 w-3.5" /></button>
              ))}</div></td>
            </tr>))}</tbody>
        </table>
        {inv.length === 0 && <div className="py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>

      <h3 className="mt-6 mb-2 font-semibold text-slate-900">{isAr ? "سجل الحركات" : "Movement log"}</h3>
      <div className="overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), isAr ? "النوع" : "Type", isAr ? "الكمية" : "Qty", isAr ? "قبل→بعد" : "Before→After", isAr ? "بواسطة" : "By"].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{moves.slice(0, 30).map((m) => (
            <tr key={m.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3">{m.product ? localize(m.product, "name") : "—"}</td>
              <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${m.direction === "in" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{m.type}</span></td>
              <td className="px-4 py-3">{m.direction === "in" ? "+" : "−"}{m.qty}</td>
              <td className="px-4 py-3 text-slate-500">{m.before}→{m.after}</td>
              <td className="px-4 py-3 text-slate-500">{m.by}</td>
            </tr>))}</tbody>
        </table>
        {moves.length === 0 && <div className="py-8 text-center text-slate-400">{t("common.none")}</div>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModal(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900">{modal.product.product ? localize(modal.product.product, "name") : ""}</h3>
            <p className="mt-1 text-sm text-slate-500">{TYPES.find((x) => x[0] === modal.type)[1]} · {isAr ? "الرصيد الحالي" : "Current"}: {modal.product.stock}</p>
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="mv-qty" placeholder={modal.type === "stocktake" ? (isAr ? "الرصيد الفعلي" : "Actual count") : (isAr ? "الكمية" : "Quantity")} className="mt-4 w-full rounded-xl border border-border px-4 py-2.5" />
            <div className="mt-5 flex justify-end gap-2"><button onClick={() => setModal(null)} className="rounded-full px-5 py-2 text-sm text-slate-600">{t("common.cancel")}</button><button onClick={submit} data-testid="mv-save" className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white">{t("common.save")}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
